import { getPhoneStoreState } from '@hit/react-jssip';

import { createOutgoingCrmRecord, updateOutgoingCall } from './salesforce-crm';

// One attempt per SIP session; repeated callbacks reuse both UUID and request.
const attempts = new WeakMap<object, Promise<void>>();
export const recordOutgoingCrm = async (
  phone: string,
  flags: { leadIntegration: boolean; outboundCallJournal: boolean }
) => {
  if (!flags.leadIntegration && !flags.outboundCallJournal) return;
  const call = Object.values(getPhoneStoreState().calls).find(
    (call) =>
      call.direction === 'outgoing' && call.remoteIdentity.number === phone
  );
  const previous = call && attempts.get(call.rtc);
  if (previous) return previous;
  const run = async () => {
    const attemptId = crypto.randomUUID();
    const pending: Array<{
      outcome: 'answered' | 'completed' | 'failed';
      durationSeconds?: number;
    }> = [];
    let record: { taskId: string; attemptId: string } | undefined;
    let acceptedAt: number | undefined;
    let chain = Promise.resolve();
    const deliver = (event: (typeof pending)[number]) => {
      if (!record) {
        pending.push(event);
        return;
      }
      const saved = record;
      chain = chain
        .then(() =>
          updateOutgoingCall(saved, event.outcome, event.durationSeconds)
        )
        .catch(() => {
          console.warn('Salesforce call result could not be updated');
        });
    };
    const accepted = () => {
      acceptedAt = Date.now();
      deliver({ outcome: 'answered' });
    };
    const ended = () =>
      deliver({
        outcome: 'completed',
        durationSeconds:
          acceptedAt === undefined
            ? undefined
            : Math.max(0, Math.floor((Date.now() - acceptedAt) / 1000)),
      });
    const failed = () => deliver({ outcome: 'failed' });
    if (flags.outboundCallJournal && call) {
      call.rtc.once('accepted', accepted);
      call.rtc.once('ended', ended);
      call.rtc.once('failed', failed);
    }
    try {
      record = await createOutgoingCrmRecord(phone, { ...flags, attemptId });
      if (record) for (const event of pending) deliver(event);
    } catch (error) {
      if (call) {
        call.rtc.removeListener('accepted', accepted);
        call.rtc.removeListener('ended', ended);
        call.rtc.removeListener('failed', failed);
      }
      throw error;
    }
  };
  const result = run();
  if (call) attempts.set(call.rtc, result);
  return result;
};
